import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Stage, Layer, Rect, Text, Group, Transformer, Line } from 'react-konva';
import { polygonLabel } from './polygon';
import type Konva from 'konva';
import type { StageProps } from 'react-konva';
import type { Section, Lot } from '../../types/inventory';
import { STATUS_CONFIG } from '../../types/inventory';
import { fitViewport, lotBounds, sectionBounds, unionBounds, zoomAt, MIN_LOT_SIZE, Viewport } from './geometry';

type Box = { x: number; y: number; width: number; height: number; rotation: number };

const CanvasStage = Stage as React.ComponentType<React.PropsWithChildren<StageProps & { width: number; height: number; x: number; y: number; scaleX: number; scaleY: number; draggable: boolean }> & React.RefAttributes<Konva.Stage>>;

interface MapCanvasProps {
  sections: Section[];
  lots: Lot[];
  selectedLotId?: string | null;
  onLotClick: (lot: Lot) => void;
  onSectionDragEnd?: (sectionId: string, x: number, y: number) => void;
  visibleLotIds: Set<string>;
  filterSection?: string;
  placementMode?: boolean;
  onPlaceLot?: (sectionId: string, x: number, y: number) => void;
  editMode?: boolean;
  disabled?: boolean;
  onLotDragEnd?: (lotId: string, x: number, y: number) => void;
  onLotResize?: (lotId: string, x: number, y: number, width: number, height: number) => void;
}

const MapCanvas: React.FC<MapCanvasProps> = ({ sections, lots, selectedLotId, onLotClick,
  onSectionDragEnd, visibleLotIds, filterSection, placementMode = false, onPlaceLot,
  editMode = false, disabled = false, onLotDragEnd, onLotResize }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const trRef = useRef<Konva.Transformer>(null);
  const nodes = useRef(new Map<string, Konva.Group>());
  const [dims, setDims] = useState({ width: 800, height: 600 });
  const [view, setView] = useState<Viewport>({ x: 0, y: 0, scale: 1 });
  const [editSelectedId, setEditSelectedId] = useState<string | null>(null);
  const [hovered, setHovered] = useState<Lot | null>(null);
  const [showLabels, setShowLabels] = useState(true);
  const fitKey = useRef('');
  const lotsBySection = useMemo(() => {
    const result = new Map<string, Lot[]>();
    lots.forEach(lot => { const group = result.get(lot.sectionId) || []; group.push(lot); result.set(lot.sectionId, group); });
    return result;
  }, [lots]);
  const labelPositions = useMemo(() => new Map(lots.filter(lot => lot.points?.length).map(lot => [lot.id, polygonLabel(lot.points!)])), [lots]);
  const activeSections = sections.filter(section => section.isActive);
  const getBounds = useCallback(() => unionBounds(sections.filter(section => section.isActive && (!filterSection || section.id === filterSection))
    .map(section => sectionBounds(section, lotsBySection.get(section.id) || []))), [sections, filterSection, lotsBySection]);
  const fit = useCallback(() => setView(fitViewport(getBounds(), dims.width, dims.height)), [getBounds, dims]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      setDims({ width: Math.max(1, entry.contentRect.width), height: Math.max(1, entry.contentRect.height) });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const key = `${filterSection || 'all'}:${sections.map(s => s.id).join(',')}:${dims.width}:${dims.height}`;
    if (fitKey.current !== key && sections.length) { fitKey.current = key; fit(); }
  }, [sections, filterSection, dims, fit]);

  useEffect(() => { if (!editMode) setEditSelectedId(null); }, [editMode]);
  useEffect(() => {
    const node = editSelectedId && visibleLotIds.has(editSelectedId) ? nodes.current.get(editSelectedId) : null;
    trRef.current?.nodes(editMode && node ? [node] : []);
    trRef.current?.getLayer()?.batchDraw();
  }, [editMode, editSelectedId, lots, visibleLotIds]);

  const zoom = (factor: number) => setView(current => zoomAt(current, { x: dims.width / 2, y: dims.height / 2 }, current.scale * factor));
  const cursor = (value: string) => { if (stageRef.current) stageRef.current.container().style.cursor = value; };
  const place = (event: Konva.KonvaEventObject<MouseEvent | TouchEvent>, section: Section) => {
    if (!placementMode || disabled || section.layoutMode === 'GRID') return;
    event.cancelBubble = true;
    const point = stageRef.current?.getRelativePointerPosition();
    if (point) onPlaceLot?.(section.id, Math.round(point.x), Math.round(point.y));
  };

  return <div className="inventory-canvas" ref={containerRef}>
    <CanvasStage ref={stageRef} width={dims.width} height={dims.height} x={view.x} y={view.y}
      scaleX={view.scale} scaleY={view.scale} draggable={!disabled && !placementMode}
      onDragEnd={(event: Konva.KonvaEventObject<DragEvent>) => { if (event.target === stageRef.current) setView(current => ({ ...current, x: event.target.x(), y: event.target.y() })); }}
      onWheel={event => {
        event.evt.preventDefault();
        const point = stageRef.current?.getPointerPosition();
        if (point) setView(current => zoomAt(current, point, current.scale * (event.evt.deltaY > 0 ? 1 / 1.1 : 1.1)));
      }}
      onMouseDown={event => { if (event.target === stageRef.current) setEditSelectedId(null); }}
      onTouchStart={event => { if (event.target === stageRef.current) setEditSelectedId(null); }}>
      <Layer>
        {activeSections.map(section => {
          const sectionLots = lotsBySection.get(section.id) || [];
          const bounds = sectionBounds(section, sectionLots);
          const active = !filterSection || filterSection === section.id;
          return <Group key={section.id} x={section.x} y={section.y} opacity={active ? 1 : 0.2}
            draggable={editMode && active && !disabled}
            onDragStart={(event: Konva.KonvaEventObject<DragEvent>) => { if (event.target === event.currentTarget) { event.cancelBubble = true; setEditSelectedId(null); cursor('grabbing'); } }}
            onDragEnd={(event: Konva.KonvaEventObject<DragEvent>) => {
              if (event.target !== event.currentTarget) return;
              event.cancelBubble = true;
              onSectionDragEnd?.(section.id, Math.round(event.target.x()), Math.round(event.target.y()));
              cursor('grab');
            }}>
            <Rect x={bounds.x - section.x} y={bounds.y - section.y} width={bounds.width} height={bounds.height}
              fill="#E7EDF0" stroke={active && editMode ? '#0D9488' : '#CBD5E1'} strokeWidth={1} cornerRadius={8}
              onClick={(event: Konva.KonvaEventObject<MouseEvent>) => place(event, section)} onTap={(event: Konva.KonvaEventObject<TouchEvent>) => place(event, section)}
              onMouseEnter={() => cursor(placementMode ? (section.layoutMode === 'GRID' ? 'not-allowed' : 'crosshair') : 'grab')}
              onMouseLeave={() => cursor('default')} />
            <Text text={`${section.name} · ${section.label}`} x={bounds.x - section.x + 12} y={bounds.y - section.y + 12}
              width={bounds.width - 24} fontSize={12} fontStyle="bold" fill="#334155" listening={false} ellipsis wrap="none" />
            {sectionLots.map(lot => {
              const box = lotBounds(lot, section);
              const visible = visibleLotIds.has(lot.id);
              const movable = editMode && section.layoutMode !== 'GRID' && visible && !disabled;
              const selected = (editMode ? editSelectedId : selectedLotId) === lot.id;
              return <Group key={lot.id} ref={(node: Konva.Group | null) => { if (node) nodes.current.set(lot.id, node); else nodes.current.delete(lot.id); }}
                x={box.x - section.x} y={box.y - section.y} opacity={visible ? 1 : 0.12} draggable={movable}
                onClick={(event: Konva.KonvaEventObject<MouseEvent>) => { event.cancelBubble = true; if (!visible || placementMode || disabled) return; if (editMode) { setEditSelectedId(movable ? lot.id : null); } else onLotClick(lot); }}
                onTap={(event: Konva.KonvaEventObject<TouchEvent>) => { event.cancelBubble = true; if (!visible || placementMode || disabled) return; if (editMode) { setEditSelectedId(movable ? lot.id : null); } else onLotClick(lot); }}
                onMouseEnter={() => { if (visible) setHovered(lot); cursor(movable ? 'grab' : editMode || !visible ? 'default' : 'pointer'); }}
                onMouseLeave={() => { setHovered(null); cursor('default'); }}
                onDragStart={(event: Konva.KonvaEventObject<DragEvent>) => { event.cancelBubble = true; setEditSelectedId(lot.id); cursor('grabbing'); }}
                onDragEnd={(event: Konva.KonvaEventObject<DragEvent>) => {
                  event.cancelBubble = true;
                  onLotDragEnd?.(lot.id, Math.round(event.target.x() + section.x), Math.round(event.target.y() + section.y));
                  cursor('grab');
                }}
                onTransformEnd={(event: Konva.KonvaEventObject<Event>) => {
                  event.cancelBubble = true;
                  const node = event.target;
                  const width = Math.max(MIN_LOT_SIZE, Math.round(box.width * node.scaleX()));
                  const height = Math.max(MIN_LOT_SIZE, Math.round(box.height * node.scaleY()));
                  node.scale({ x: 1, y: 1 });
                  onLotResize?.(lot.id, Math.round(node.x() + section.x), Math.round(node.y() + section.y), width, height);
                }}>
                {lot.points?.length ? <>
                  <Rect width={box.width} height={box.height} fill="transparent" listening={false} />
                  <Line points={lot.points.flatMap(point => [point.x * box.width, point.y * box.height])} closed
                    fill={STATUS_CONFIG[lot.status]?.color || '#64748B'} stroke={selected ? '#0F172A' : '#FFFFFF'} strokeWidth={selected ? 2.5 : 1} lineJoin="round" />
                </> : <Rect width={box.width} height={box.height} fill={STATUS_CONFIG[lot.status]?.color || '#64748B'}
                  stroke={selected ? '#0F172A' : '#FFFFFF'} strokeWidth={selected ? 2.5 : 1} cornerRadius={3} />}
                {showLabels && <Text text={lot.lotNumber}
                  x={lot.points?.length ? Math.max(0, Math.min(box.width * .5, (labelPositions.get(lot.id)?.x || .5) * box.width - box.width * .25)) : 0}
                  y={lot.points?.length ? Math.max(0, Math.min(box.height - 12, (labelPositions.get(lot.id)?.y || .5) * box.height - 6)) : 0}
                  width={lot.points?.length ? box.width * .5 : box.width} height={lot.points?.length ? 12 : box.height} align="center" verticalAlign="middle"
                  fontSize={box.width < 36 ? 8 : 10} fontStyle="bold" fill={['LOCKED', 'BLOCKED'].includes(lot.status) ? '#FFFFFF' : '#020617'} listening={false} />}
                {lot.isLocked && <Rect x={box.width - 8} y={3} width={5} height={5} fill="#0F172A" listening={false} />}
              </Group>;
            })}
          </Group>;
        })}
        {editMode && <Transformer ref={trRef} rotateEnabled={false} keepRatio={false} flipEnabled={false}
          ignoreStroke anchorSize={10} anchorCornerRadius={2} borderStroke="#0D9488" anchorFill="#FFFFFF" anchorStroke="#0D9488"
          boundBoxFunc={(oldBox: Box, newBox: Box) => newBox.width < MIN_LOT_SIZE * view.scale || newBox.height < MIN_LOT_SIZE * view.scale ? oldBox : newBox} />}
      </Layer>
    </CanvasStage>
    <div className="inventory-map-tools" role="group" aria-label="Map controls">
      <button type="button" onClick={() => zoom(1 / 1.2)} aria-label="Zoom out">−</button>
      <output aria-label="Map zoom">{Math.round(view.scale * 100)}%</output>
      <button type="button" onClick={() => zoom(1.2)} aria-label="Zoom in">+</button>
      <button type="button" onClick={fit}>Fit map</button>
      <button type="button" aria-pressed={showLabels} onClick={() => setShowLabels(value => !value)}>Labels</button>
    </div>
    {hovered && <div className="inventory-map-tooltip" role="status">{hovered.lotNumber} · {STATUS_CONFIG[hovered.status].label}{hovered.isLocked ? ' · Locked' : ''}</div>}
  </div>;
};

export default MapCanvas;
